import React, { memo, useCallback } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BottomTabBarProps } from '@react-navigation/bottom-tabs';

import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { common, designSystem } from '@/styles/styles';

const { colors } = designSystem;

export const BOTTOM_TABS_HEIGHT = 60;

type IconProps = (props: { fill?: string; width?: number; height?: number }) => React.JSX.Element;

const TAB_ICON_SIZE = 24;

const borderTopStyles = { borderTopWidth: 1, borderColor: ds.colorValue.border.strong };

type TabBarButtonProps = {
    isFocused: boolean;
    icon: IconProps;
    title: string;
    routeKey: string;
    routeName: string;
    badge?: string | number;
    onPress: (routeKey: string, routeName: string, isFocused: boolean) => void;
};

const TabBarButton = memo(
    ({ isFocused, icon: Icon, title, routeKey, routeName, badge, onPress }: TabBarButtonProps) => {
        const handlePress = useCallback(() => {
            onPress(routeKey, routeName, isFocused);
        }, [onPress, routeKey, routeName, isFocused]);

        return (
            <TouchableOpacity onPress={handlePress} testID={routeName} style={styles.pressable}>
                <View style={[styles.selfCentered, styles.iconWrapper]}>
                    <Icon
                        fill={isFocused ? ds.colorValue.icon.brand : colors.neutral300}
                        width={TAB_ICON_SIZE}
                        height={TAB_ICON_SIZE}
                    />
                    {badge !== undefined ? (
                        <View style={styles.badge}>
                            <Text
                                variant={'labelSmall'}
                                fontWeight="600"
                                textAlign={'center'}
                                fontSize={10}
                                lineHeight={14}
                                letterSpacing={-0.4}
                                color={ds.colorToken.text.primary}
                            >
                                {badge}
                            </Text>
                        </View>
                    ) : null}
                </View>
                <View style={styles.selfCentered}>
                    <Text
                        variant={'labelSmall'}
                        fontWeight="600"
                        fontSize={10}
                        lineHeight={12}
                        letterSpacing={0}
                        color={isFocused ? ds.colorToken.text.brand : 'neutral300'}
                    >
                        {title}
                    </Text>
                </View>
            </TouchableOpacity>
        );
    }
);

export const TabBar = ({ state, navigation, descriptors }: BottomTabBarProps) => {
    // RAF bar, PICK bar and Arcade product doesn't need borderTop applied - both are a bar on top of the tab bar
    const product = useJurisdictionStore(jurisdictionState => jurisdictionState?.product);
    const isArcadeProduct = product === Product.Arcade;
    const tabBarStyles = isArcadeProduct ? styles.root : [styles.root, borderTopStyles];

    const handlePress = useCallback(
        (routeKey: string, routeName: string, isFocused: boolean) => {
            const event = navigation.emit({
                type: 'tabPress',
                target: routeKey,
                canPreventDefault: true,
            });

            if (!isFocused && !event.defaultPrevented) {
                navigation.navigate(routeName);
            }
        },
        [navigation]
    );

    return (
        <>
            <View style={tabBarStyles}>
                <SafeAreaView edges={['right', 'bottom', 'left']}>
                    <View style={[common.spaceBetweenRow]}>
                        {state.routes.map((route, index) => {
                            const isFocused = state.index === index;
                            const { options } = descriptors[route.key];

                            return (
                                <TabBarButton
                                    key={route.key}
                                    isFocused={isFocused}
                                    icon={options.tabBarIcon as IconProps}
                                    title={options.title ?? route.name}
                                    routeKey={route.key}
                                    routeName={route.name}
                                    badge={options.tabBarBadge}
                                    onPress={handlePress}
                                />
                            );
                        })}
                    </View>
                </SafeAreaView>
            </View>
        </>
    );
};

const styles = StyleSheet.create({
    root: {
        backgroundColor: ds.colorValue.background.neutral.base,
        paddingVertical: 10,
        paddingHorizontal: 8,
    },
    selfCentered: { alignSelf: 'center' },
    iconWrapper: { paddingBottom: 4 },
    badge: {
        position: 'absolute',
        top: -8,
        left: 15,
        minWidth: 16,
        height: 15,
        paddingHorizontal: 3,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ds.colorValue.background.brand.strong,
    },
    walletWrapper: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    pressable: {
        flex: 1,
    },
});
